import { AnalyticsProvider, type AnalyticsConfig } from "@repo/analytics";
import { CookieBanner, useCookieConsent } from "@repo/consent";
import { css } from "@repo/styled-system/css";
import type { ReactNode } from "react";

/**
 * Gates PostHog behind cookie consent in the staff SPA (features/privacy-and-consent.md §C.12). Wraps
 * `AnalyticsProvider` and only lets it initialise once the operator grants the `analytics` category —
 * so no PostHog network request or cookie is made before consent, and withdrawing it opts out for the
 * session. Client-side receipt only (localStorage): the venue-scoped server receipt endpoint isn't
 * reachable here, above the auth/venue context.
 *
 * Bump `CONSENT_POLICY_VERSION` when the cookie text changes to force a fresh prompt.
 */
const CONSENT_POLICY_VERSION = "2026-09-01";

export function ConsentedAnalytics({
  config,
  children,
}: {
  config: AnalyticsConfig;
  children: ReactNode;
}) {
  const consent = useCookieConsent({ policyVersion: CONSENT_POLICY_VERSION });
  return (
    <AnalyticsProvider config={config} enabled={consent.isGranted("analytics")}>
      {children}
      <div className={bannerWrapper}>
        <CookieBanner consent={consent} />
      </div>
    </AnalyticsProvider>
  );
}

// Accept-all and reject-all share the SAME prominent style — declining must be as easy and visible as
// accepting (CNIL). "Personnaliser" / "Enregistrer" are the secondary/outline action.
const primaryButton = {
  colorPalette: "brand",
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  borderColor: "colorPalette.default",
  // Re-assert the brand bg on hover so the base `& dialog button` hover (bg.subtle) can't leak in and
  // wash the button out — just dim it slightly.
  _hover: { bg: "colorPalette.default", color: "colorPalette.fg", opacity: 0.9 },
} as const;

const bannerWrapper = css({
  "& dialog": {
    position: "fixed",
    insetInline: "0",
    bottom: "0",
    zIndex: "100",
    mx: "auto",
    m: { base: "3", md: "4" },
    maxW: "md",
    w: "auto",
    borderWidth: "1px",
    borderColor: "border.default",
    borderRadius: "lg",
    bg: "bg.default",
    color: "fg.default",
    boxShadow: "lg",
    p: "4",
  },
  "& dialog h2": { textStyle: "md", fontWeight: "semibold", mb: "1" },
  "& dialog > p": { color: "fg.muted", textStyle: "sm", mb: "4" },
  "& dialog fieldset": {
    borderWidth: "0",
    p: "0",
    m: "0",
    mb: "4",
    display: "flex",
    flexDirection: "column",
    gap: "3",
  },
  "& dialog legend": { fontWeight: "medium", mb: "2" },
  "& dialog fieldset label": {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: "2",
    alignItems: "baseline",
    textStyle: "sm",
  },
  "& dialog fieldset label span:last-child": {
    gridColumn: "2",
    color: "fg.muted",
    textStyle: "xs",
  },
  "& dialog > div": { display: "flex", flexWrap: "wrap", gap: "2", justifyContent: "flex-end" },
  "& dialog button": {
    px: "4",
    py: "2",
    borderRadius: "md",
    textStyle: "sm",
    fontWeight: "medium",
    cursor: "pointer",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.default",
    color: "fg.default",
    _hover: { bg: "bg.subtle" },
  },
  '& dialog button[data-testid="accept-all"]': primaryButton,
  '& dialog button[data-testid="reject-all"]': primaryButton,
});
